import { audioManager } from '../../AudioManager.js';
import ActionType from './ActionType.js';
import { keyKPressInstant, keyLPressInstant } from '../../BattleAnimation.js';
import { getOSM, getBattleStateMachine, getBattleGUI } from '../../OverworldAnimation.js';
import { ANIMATION_SPEED } from '../../constants.js';

export default class BattleAction {
    constructor({
        type,
        description,
        animationFunction,
        audioFilePath,
        text = '',
        delay = 0,
        delayCursor = 0,
        useCursor = true,
        textCursor = 0,
        startAction = () => {},
        action = () => {},
        endAction = () => {},
        debounce = 0,
        playbackRate = 1,
        context = {},
        duration = 0,
        pokemon = null,
        clearOnFaint = false,
    }) {
        this.pokemon = pokemon;
        this.clearOnFaint = clearOnFaint;
        this.type = type;
        this.description = description;
        this.animationFunction = animationFunction; // Return true if animation is done
        this.audioFile = audioFilePath;
        this.text = text;
        this.action = action;
        this.delay = delay;
        this.delayCursor = delayCursor;
        this.textCursor = textCursor;
        this.started = false;
        this.ended = false;
        this.startAction = startAction;
        this.endAction = endAction;
        this.useCursor = useCursor;
        this.debounceCursor = 0;
        this.debounce = debounce;
        this.playbackRate = playbackRate;
        this.context = context;
        this.duration = duration;
    }

    start() {
        // Only runs once at the beginning of an action
        if (this.started) {
            return;
        }
        console.debug(this.description ?? 'No description provided...');
        this.startAction();
        this.started = true;
        this.delayCursor = 0;
        this.textCursor = 0;
        this.debounceCursor = 0;

        if (this.type === ActionType.FINISH_READING) {
            const battleGUI = getBattleGUI();
            if (battleGUI) battleGUI.text.showContinueReadingArrow = true;
        }
    }

    execute() {
        this.start();
        this.duration -= 1;
        switch (this.type) {
            case ActionType.ANIMATION:
                this.ended = this.animationFunction();
                break;
            case ActionType.AUDIO:
                audioManager.play(this.audioFile);
                this.ended = true;
                break;
            case ActionType.TEXT: {
                const bsm = getBattleStateMachine();
                const battleGUI = getBattleGUI();
                // Setting text
                if (bsm) bsm.battleText = this.text;

                // Get text speed increment from options menu
                const textSpeedIncrement = getOSM().getTextSpeedIncrement();

                // Increment cursor based on speed setting
                this.textCursor += textSpeedIncrement;
                if (bsm) bsm.cursor = this.useCursor
                    ? Math.floor(this.textCursor)
                    : this.text.length + 1;
                if (battleGUI) battleGUI.cursor = this.useCursor
                    ? Math.floor(this.textCursor)
                    : this.text.length + 1;
                this.ended = this.useCursor ? this.textCursor > this.text.length : true;
                if (this.ended) {
                    if (this.delay > 0) {
                        this.delayCursor += 1 * ANIMATION_SPEED;
                        this.ended = this.delayCursor >= this.delay;
                    } else {
                        return this.ended;
                    }
                }
                break;
            }
            case ActionType.DELAY:
                this.delayCursor += 1 * ANIMATION_SPEED;
                this.ended = this.delayCursor >= this.delay;
                break;
            case ActionType.INPUT:
                this.debounceCursor += 1;
                if (this.debounceCursor < this.debounce) {
                    return;
                }
                this.ended = this.action();
                break;
            case ActionType.FINISH_READING:
                this.debounceCursor += 1;
                if (this.debounceCursor < this.debounce) {
                    break;
                }
                this.ended = (keyLPressInstant || keyKPressInstant) && this.debounceCursor > this.debounce;
                break;
            case ActionType.CODE:
                this.ended = this.action();
                {
                    const battleGUI = getBattleGUI();
                    if (battleGUI) battleGUI.text.showContinueReadingArrow = false;
                }
                break;
        }
        this.end();
    }

    end() {
        // Only runs once after the action completes
        if (!this.ended) {
            return;
        }
        if (this.type === ActionType.FINISH_READING) {
            const battleGUI = getBattleGUI();
            if (battleGUI) battleGUI.text.showContinueReadingArrow = false;
        }
        this.endAction();
    }
}
